import { useEffect, useMemo, useState } from 'react';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { SYSTEM_FONT_FAMILIES } from './designDnaV2SystemFonts';

const chunk = (items, size) => Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, i * size + size));

function ensureSystemFontLinks() {
  if (typeof document === 'undefined' || document.getElementById('sketchario-v2-font-picker-links')) return;
  const holder = document.createElement('div');
  holder.id = 'sketchario-v2-font-picker-links';
  holder.style.display = 'none';
  chunk(SYSTEM_FONT_FAMILIES, 6).forEach((group, index) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.dataset.index = String(index);
    link.href = `https://fonts.googleapis.com/css2?${group.map(({ family }) => `family=${encodeURIComponent(family).replace(/%20/g, '+')}`).join('&')}&display=swap`;
    document.head.appendChild(link);
  });
  document.body.appendChild(holder);
}

function ensureUploadedFaces(fonts) {
  if (typeof document === 'undefined') return;
  let style = document.getElementById('sketchario-v2-uploaded-font-faces');
  if (!style) {
    style = document.createElement('style');
    style.id = 'sketchario-v2-uploaded-font-faces';
    document.head.appendChild(style);
  }
  style.textContent = (fonts || []).filter(f => f.url).map(f => {
    const family = `SketcharioPicker-${f.asset_id}`;
    const format = f.format === 'otf' ? 'opentype' : f.format === 'ttf' ? 'truetype' : f.format || 'woff2';
    return `@font-face{font-family:'${family}';src:url('${f.url}') format('${format}');font-weight:${Number(f.weight)||400};font-style:${f.style==='italic'?'italic':'normal'};font-display:swap;}`;
  }).join('\n');
}

export default function DesignDnaV2FontPicker({ api, projectId, fonts = [], value = '', onSelect, onFontsChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState('');

  useEffect(() => { ensureSystemFontLinks(); }, []);
  useEffect(() => { ensureUploadedFaces(fonts); }, [fonts]);

  const current = fonts.find(f => f.asset_id === value);
  const uploaded = useMemo(() => fonts.filter(f => f.source !== 'system'), [fonts]);
  const system = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SYSTEM_FONT_FAMILIES.filter(item => !q || `${item.family} ${item.category}`.toLowerCase().includes(q));
  }, [query]);

  const selectSystem = async (family) => {
    const existing = fonts.find(f => f.source === 'system' && f.family === family && Number(f.weight || 400) === 400 && (f.style || 'normal') === 'normal');
    if (existing) { onSelect(existing.asset_id); setOpen(false); return; }
    setLoading(family);
    try {
      const { data } = await api.post(`/projects/${projectId}/design/v2/fonts/system`, { family, weight: 400, style: 'normal' });
      onFontsChange?.(data);
      onSelect(data.asset_id);
      setOpen(false);
    } catch (e) {
      toast.error(`${family}: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(''); }
  };

  return <div className="relative" data-testid="design-dna-v2-font-picker">
    <button type="button" className="input-dark text-xs w-full flex items-center justify-between gap-2 text-left" onClick={() => setOpen(v => !v)}>
      <span style={current ? { fontFamily: `'SketcharioPicker-${current.asset_id}', '${current.family}', sans-serif`, fontWeight: current.weight || 400, fontStyle: current.style || 'normal' } : undefined}>
        {current?.family || 'Da reference / suggerito'}
      </span>
      <CaretDown size={13}/>
    </button>
    {open && <div className="absolute z-50 mt-2 left-0 right-0 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary,#171717)] shadow-2xl overflow-hidden">
      <div className="p-2 border-b border-[var(--border-color)] relative"><MagnifyingGlass size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"/><input autoFocus className="input-dark text-xs w-full pl-8" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Cerca font…"/></div>
      <div className="max-h-72 overflow-y-auto p-2 space-y-1">
        {uploaded.map(font => <button key={font.asset_id} type="button" onClick={()=>{onSelect(font.asset_id);setOpen(false);}} className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-base" style={{fontFamily:`'SketcharioPicker-${font.asset_id}', '${font.family}', sans-serif`,fontWeight:font.weight||400,fontStyle:font.style||'normal'}}>{font.family}</button>)}
        {uploaded.length > 0 && <div className="border-t border-[var(--border-color)] my-2"/>}
        {system.map(item => <button key={item.family} disabled={loading===item.family} type="button" onClick={()=>selectSystem(item.family)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-base disabled:opacity-50" style={{fontFamily:`'${item.family}', sans-serif`}}>{loading===item.family?'Caricamento…':item.family}</button>)}
      </div>
    </div>}
  </div>;
}
